import { useCallback } from 'react';

export default function useGameActions({
  socket, connectionStatus, persistentIdRef, saveSession, clearSession,
  game, gameDispatch, round, roundDispatch,
}) {
  const { roomCode, playerName, gameOptions, isGameController } = game;

  const syncStateFromServer = useCallback((dto) => {
    gameDispatch({ type: 'STATE_UPDATE', dto });
    roundDispatch({ type: 'STATE_UPDATE', dto });
  }, [gameDispatch, roundDispatch]);

  const joinGame = useCallback((e, avatarData = null) => {
    e.preventDefault();
    if (!roomCode.trim() || !playerName.trim()) return;
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    socket.emit('join-room-as-player', {
      roomCode: roomCode.toUpperCase(),
      playerName: playerName.trim(),
      avatar: avatarData,
    }, (response) => {
      if (response.success) {
        const currentPlayer = response.players.find(p => p.id === response.playerId);
        gameDispatch({
          type: 'JOIN_SUCCESS',
          playerId: response.playerId,
          players: response.players,
          isGameController: response.isGameController,
          playerScore: currentPlayer?.score ?? 0,
          statusMessage: response.isGameController
            ? 'You are the Host! You can start the game when ready.'
            : 'Joined successfully! Waiting for game to start...',
          gameOptions: response.gameOptions,
        });

        if (response.persistentId) {
          saveSession(roomCode.toUpperCase(), playerName.trim(), response.persistentId);
        }
      } else {
        gameDispatch({ type: 'SET_STATUS', statusMessage: `Failed to join: ${response.message}` });
      }
    });
  }, [socket, connectionStatus, roomCode, playerName, saveSession, gameDispatch]);

  const submitPromptWithText = useCallback((text) => {
    if (!text.trim()) return;
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    socket.emit('submit-prompt', { roomCode, prompt: text.trim() });
    roundDispatch({ type: 'PROMPT_SUBMITTED', prompt: text.trim() });
  }, [socket, connectionStatus, roomCode, gameDispatch, roundDispatch]);

  const submitDrawing = useCallback((data) => {
    if (!data) return;
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    socket.emit('submit-response', {
      roomCode,
      response: data,
      isDrawing: true,
    });

    roundDispatch({ type: 'DRAWING_SUBMITTED', data });
    gameDispatch({ type: 'SET_STATUS', statusMessage: 'Drawing submitted! Waiting for other players...' });
  }, [socket, connectionStatus, roomCode, gameDispatch, roundDispatch]);

  const submitVote = useCallback((votedForPlayerId) => {
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    socket.emit('submit-vote', { roomCode, votedForPlayerId });
    roundDispatch({ type: 'VOTE_SUBMITTED' });
    gameDispatch({ type: 'SET_STATUS', statusMessage: 'Vote submitted! Waiting for results...' });
  }, [socket, connectionStatus, roomCode, gameDispatch, roundDispatch]);

  const startGame = useCallback(() => {
    if (!socket || !socket.connected) {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    console.log('Emitting start-game', { roomCode, gameOptions });
    socket.emit('start-game', { roomCode, gameOptions }, (response) => {
      if (response && !response.success) {
        gameDispatch({ type: 'SET_STATUS', statusMessage: response.message || 'Failed to start game' });
      }
    });
    gameDispatch({ type: 'SET_STATUS', statusMessage: 'Starting game...' });
  }, [socket, roomCode, gameOptions, gameDispatch]);

  const handleGameOptionChange = useCallback((optionKey, value) => {
    if (!isGameController) return;
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    gameDispatch({ type: 'SET_GAME_OPTION', key: optionKey, value });
    socket.emit('update-game-options', {
      roomCode,
      options: { ...gameOptions, [optionKey]: value },
    });
  }, [socket, connectionStatus, roomCode, gameOptions, isGameController, gameDispatch]);

  const submitJudgeAward = useCallback((data) => {
    roundDispatch({ type: 'JUDGE_AWARD_SUBMITTED' });
    if (socket) socket.emit('submit-judge-award', { roomCode, award: data });
  }, [socket, roomCode, roundDispatch]);

  const reconnectToRoom = useCallback(() => {
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }
    if (!roomCode || !playerName) return;

    socket.emit('rejoin-room', { roomCode, playerName, persistentId: persistentIdRef.current }, (response) => {
      if (response.success) {
        syncStateFromServer(response);
        if (response.persistentId) saveSession(roomCode, playerName, response.persistentId);
        gameDispatch({ type: 'SET_STATUS', statusMessage: 'Reconnected!' });
      } else {
        gameDispatch({ type: 'SET_STATUS', statusMessage: `Reconnect failed: ${response.message}` });
      }
    });
  }, [socket, connectionStatus, roomCode, playerName, persistentIdRef, saveSession,
      syncStateFromServer, gameDispatch]);

  const handleJoinNewGame = useCallback(() => {
    if (socket && roomCode) {
      socket.emit('leave-room', { roomCode });
    }
    clearSession();
    gameDispatch({ type: 'RESET' });
    roundDispatch({ type: 'RESET' });
  }, [socket, roomCode, clearSession, gameDispatch, roundDispatch]);

  const usePowerup = useCallback((powerupId, targetPlayerId) => {
    if (!socket || connectionStatus !== 'connected') return;
    socket.emit('use-powerup', { roomCode, powerupId, targetPlayerId }, (response) => {
      if (response?.success) {
        roundDispatch({ type: 'POWERUP_USED', powerupId });
      }
    });
  }, [socket, connectionStatus, roomCode, roundDispatch]);

  const handleStartNewGame = useCallback(() => {
    if (!socket || connectionStatus !== 'connected') {
      gameDispatch({ type: 'SET_STATUS', statusMessage: 'Connection lost. Trying to reconnect...' });
      return;
    }

    socket.emit('reset-game', { roomCode });
    gameDispatch({ type: 'SET_STATUS', statusMessage: 'Starting new game in same room...' });
  }, [socket, connectionStatus, roomCode, gameDispatch]);

  return {
    joinGame,
    submitPrompt: submitPromptWithText,
    submitDrawing,
    submitVote,
    startGame,
    handleGameOptionChange,
    submitJudgeAward,
    reconnectToRoom,
    handleJoinNewGame,
    handleStartNewGame,
    syncStateFromServer,
    usePowerup,
  };
}
